import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  tone,
  useCopyGap,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { useInteriorLandingT, usePageVars } from './kit';
import Showcase from './Showcase';
import StartCard from './StartCard';
import type { InteriorLandingPage } from './types';

/**
 * Headline and the way in first: nine in ten people who click through do it
 * from the first screen. The proof (a real before and after) sits beside it
 * on wide screens and right under it on phones.
 */
export default function InteriorHero({ page }: { page: InteriorLandingPage }) {
  const { t } = useInteriorLandingT();
  const gap = useCopyGap();
  const vars = usePageVars(page);

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-5 pb-6 pt-8 sm:px-6 sm:pt-14 lg:px-8 lg:pb-10'>
        <div className='grid grid-cols-1 gap-x-12 gap-y-10 lg:grid-cols-12 lg:items-center xl:gap-x-16'>
          <div className='min-w-0 lg:col-span-6'>
            <p
              className={clsxm(
                styles.enter,
                'inline-flex max-w-full items-center gap-2 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                tone.body,
              )}
            >
              <span className='flex-none whitespace-nowrap rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
                {t(page.badge ?? 'Free to try')}
              </span>
              <span className='truncate'>{t(page.eyebrow, vars)}</span>
            </p>

            <h1
              className={clsxm(
                styles.display,
                styles.heroTitle,
                styles.enter,
                'mt-5 text-[2.5rem] font-semibold leading-[1.04] sm:mt-6 sm:text-6xl sm:leading-[1.02] lg:text-[3.5rem] xl:text-[4rem]',
                tone.ink,
              )}
              style={delay(60)}
            >
              {t(page.title, vars)}
              {page.titleTail ? (
                <>
                  {gap}
                  <span className='text-primary-600 dark:text-primary-400'>
                    {t(page.titleTail, vars)}
                  </span>
                </>
              ) : null}
            </h1>

            <p
              className={clsxm(
                styles.enter,
                'mt-5 max-w-xl text-[17px] leading-7 sm:mt-6 sm:text-[1.2rem] sm:leading-[1.65]',
                tone.body,
              )}
              style={delay(140)}
            >
              {t(page.lead, vars)}
            </p>

            <Points
              page={page}
              className={clsxm(styles.enter, 'mt-6 hidden sm:flex lg:hidden')}
            />

            <div
              className={clsxm(styles.enter, 'mt-7 max-w-xl sm:mt-8')}
              style={delay(220)}
            >
              <StartCard page={page} />
            </div>
          </div>

          <div
            className={clsxm(styles.enter, 'min-w-0 lg:col-span-6')}
            style={delay(120)}
          >
            <Showcase page={page} />
            <Points page={page} className='mt-7 hidden lg:flex' />
          </div>
        </div>
      </div>
    </header>
  );
}

/** The page's three promises (beside the image on wide screens). */
function Points({
  page,
  className,
}: {
  page: InteriorLandingPage;
  className?: string;
}) {
  const { t } = useInteriorLandingT();
  const vars = usePageVars(page);
  return (
    <ul
      className={clsxm(
        'flex-wrap gap-x-5 gap-y-2 text-sm font-medium',
        tone.body,
        className,
      )}
    >
      {page.points.map((point) => (
        <li key={point} className='flex items-center gap-2'>
          <span className='flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'>
            <CheckGlyph className='h-3 w-3' />
          </span>
          {t(point, vars)}
        </li>
      ))}
    </ul>
  );
}
